import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";

export function safeMarkdownUrl(url: string) {
  if (url.startsWith("#") || (url.startsWith("/") && !url.startsWith("//"))) {
    return url;
  }

  try {
    const parsed = new URL(url);

    if (["http:", "https:", "mailto:"].includes(parsed.protocol)) {
      return url;
    }
  } catch {
    return "";
  }

  return "";
}

export function SafeMarkdown({ children }: { children: string }) {
  return (
    <div className="min-w-0 max-w-full [overflow-wrap:anywhere]">
      <ReactMarkdown
        components={{
          a: ({ node: _node, href, children: linkChildren, ...props }) => {
            if (!href) {
              return <span>{linkChildren}</span>;
            }

            const external =
              href.startsWith("http:") || href.startsWith("https:");

            return (
              <a
                {...props}
                className="break-all font-medium underline underline-offset-2 focus-visible:rounded-sm focus-visible:outline-none focus-visible:ring-[3px] focus-visible:ring-ring/50"
                href={href}
                rel={external ? "noopener noreferrer" : undefined}
                target={external ? "_blank" : undefined}
              >
                {linkChildren}
              </a>
            );
          },
          blockquote: ({ node: _node, ...props }) => (
            <blockquote
              className="border-slate-300 border-l-2 pl-3 text-slate-600"
              {...props}
            />
          ),
          code: ({ node: _node, className, ...props }) => (
            <code
              className={`break-words rounded bg-slate-100 px-1 py-0.5 font-mono text-slate-800 text-xs [pre_&]:break-normal [pre_&]:bg-transparent [pre_&]:p-0 ${className ?? ""}`}
              {...props}
            />
          ),
          img: () => null,
          ol: ({ node: _node, ...props }) => (
            <ol className="ml-5 list-decimal space-y-1" {...props} />
          ),
          p: ({ node: _node, ...props }) => (
            <p
              className="whitespace-pre-wrap [overflow-wrap:anywhere]"
              {...props}
            />
          ),
          pre: ({ node: _node, ...props }) => (
            <pre
              className="max-w-full overflow-x-auto rounded-lg bg-slate-100 p-3"
              {...props}
            />
          ),
          table: ({ node: _node, ...props }) => (
            <div className="max-w-full overflow-x-auto">
              <table
                className="w-full border-collapse text-left text-sm"
                {...props}
              />
            </div>
          ),
          td: ({ node: _node, ...props }) => (
            <td className="border border-slate-200 px-2 py-1" {...props} />
          ),
          th: ({ node: _node, ...props }) => (
            <th
              className="border border-slate-200 bg-slate-50 px-2 py-1"
              {...props}
            />
          ),
          ul: ({ node: _node, ...props }) => (
            <ul className="ml-5 list-disc space-y-1" {...props} />
          ),
        }}
        remarkPlugins={[remarkGfm]}
        skipHtml
        urlTransform={safeMarkdownUrl}
      >
        {children}
      </ReactMarkdown>
    </div>
  );
}
